import { useUIStore } from '@/config/stores/ui-store';
import ColorModeIconDropdown from '@/config/theme/color-mode/color-mode-icon-dropdown';
import { HUB_EVENTS, hubEmitter } from '@/core/realtime';
import { useAuthStore } from '@/features/auth';
import { useSettingsStore } from '@/features/retrieval/stores/settings';
import { NAVBAR_HEIGHT } from '@/layout';
import { GradientAvatar } from '@/shared/components';
import {
  Language as LanguageIcon,
  Menu as MenuIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Wifi as WifiIcon
} from '@mui/icons-material';
import {
  AppBar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Conversation } from '../models/conversation';
import { useConversationMembers } from '../queries/member.queries';

interface NavbarProps {
  conv?: Nullable<Conversation>;
  onMenuClick?: () => void;
  showMenuButton?: boolean;
}

export const ConversationNavbar = ({ conv, onMenuClick, showMenuButton = false }: NavbarProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { toggleAside, friends, toggleMessageSearchDrawer } = useUIStore();
  const { user } = useAuthStore();
  const { language, setLanguage } = useSettingsStore();
  const [langMenuAnchor, setLangMenuAnchor] = useState<null | HTMLElement>(null);

  const members = useConversationMembers(conv?.conversationId);
  const hasSomeoneOnline = useMemo(() => {
    if (!user?.user_id || !Array.isArray(members?.data)) return false;
    const userIds = friends
      .filter((f) => f.presenceStatus === 'Online' && f.userId !== user?.user_id)
      .map((f) => f.userId);
    return members.data.some((m) => userIds.includes(m.userId));
  }, [friends, members?.data, user?.user_id]);

  const [connectionStatus, setConnectionStatus] = useState<
    'disconnected' | 'connecting' | 'connected' | 'reconnecting' | 'error'
  >('disconnected');

  useEffect(() => {
    const handleConnecting = () => setConnectionStatus('connecting');
    const handleConnected = () => setConnectionStatus('connected');
    const handleReconnecting = () => setConnectionStatus('reconnecting');
    const handleReconnected = () => setConnectionStatus('connected');
    const handleDisconnected = () => setConnectionStatus('disconnected');
    const handleError = () => setConnectionStatus('error');

    hubEmitter.on(HUB_EVENTS.CONNECTION.CONNECTING, handleConnecting);
    hubEmitter.on(HUB_EVENTS.CONNECTION.CONNECTED, handleConnected);
    hubEmitter.on(HUB_EVENTS.CONNECTION.RECONNECTING, handleReconnecting);
    hubEmitter.on(HUB_EVENTS.CONNECTION.RECONNECTED, handleReconnected);
    hubEmitter.on(HUB_EVENTS.CONNECTION.DISCONNECTED, handleDisconnected);
    hubEmitter.on(HUB_EVENTS.CONNECTION.ERROR, handleError);

    return () => {
      hubEmitter.off(HUB_EVENTS.CONNECTION.CONNECTING, handleConnecting);
      hubEmitter.off(HUB_EVENTS.CONNECTION.CONNECTED, handleConnected);
      hubEmitter.off(HUB_EVENTS.CONNECTION.RECONNECTING, handleReconnecting);
      hubEmitter.off(HUB_EVENTS.CONNECTION.RECONNECTED, handleReconnected);
      hubEmitter.off(HUB_EVENTS.CONNECTION.DISCONNECTED, handleDisconnected);
      hubEmitter.off(HUB_EVENTS.CONNECTION.ERROR, handleError);
    };
  }, []);

  const getSubtitle = () => {
    if (!conv) {
      return t('layout.navbar.selectToStart');
    }
    return hasSomeoneOnline ? t('layout.navbar.online') : t('layout.navbar.offline');
  };

  const getTitle = () => {
    return conv?.conversationName || t('layout.navbar.selectConversation');
  };

  const getStatusTooltip = () => {
    switch (connectionStatus) {
      case 'connected':
        return t('layout.navbar.connectionStatus.connected');
      case 'connecting':
        return t('layout.navbar.connectionStatus.connecting');
      case 'reconnecting':
        return t('layout.navbar.connectionStatus.reconnecting');
      case 'disconnected':
        return t('layout.navbar.connectionStatus.disconnected');
      case 'error':
        return t('layout.navbar.connectionStatus.error');
      default:
        return t('layout.navbar.connectionStatus.unknown');
    }
  };

  const handleLanguageMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLangMenuAnchor(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLangMenuAnchor(null);
  };

  const handleLanguageChange = (lang: 'en' | 'vi') => {
    setLanguage(lang);
    handleLanguageMenuClose();
  };

  return (
    <AppBar
      position='static'
      elevation={1}
      sx={{
        backgroundColor: 'background.paper',
        color: 'text.primary',
        borderBottom: 1,
        borderColor: 'divider',
        height: NAVBAR_HEIGHT
      }}
    >
      <Toolbar
        sx={{
          minHeight: '4rem',
          px: { xs: 1.5, sm: 3 },
          py: 1
        }}
      >
        {/* Menu button for mobile */}
        {showMenuButton && isMobile && (
          <IconButton edge='start' onClick={onMenuClick} sx={{ mr: { xs: 1, sm: 1.5 } }}>
            <MenuIcon />
          </IconButton>
        )}

        {/* Group Chat Info - Responsive */}
        <Stack
          direction='row'
          alignItems='center'
          sx={{
            height: '100%',
            cursor: 'pointer',
            gap: 1,
            borderRadius: '8px',
            p: 1,
            px: 1.5,
            '&:hover': { backgroundColor: 'action.hover' }
          }}
          component='div'
          onClick={toggleAside}
        >
          <GradientAvatar
            peerId={conv?.conversationId}
            src={conv?.conversationAvatarRef}
            name={conv?.conversationName}
            variant={conv?.conversationType === 'GROUP' ? 'rounded' : 'circular'}
            sx={{ width: 40, height: 40 }}
          />

          <Box>
            <Typography variant={'subtitle1'} noWrap sx={{ fontSize: { xs: 12, sm: 14, md: 16 } }}>
              {getTitle()}
            </Typography>
            <Typography variant='caption' color='text.secondary' noWrap sx={{ fontSize: { xs: 11, sm: 13 } }}>
              {getSubtitle()}
            </Typography>
          </Box>
        </Stack>
        {/* Header Actions - Responsive */}
        <Stack
          direction='row'
          spacing={{ xs: 0.5, sm: 1 }}
          sx={{ flex: 1, alignItems: 'center', justifyContent: 'flex-end' }}
        >
          <Tooltip title={getStatusTooltip()}>
            <IconButton size={isMobile ? 'small' : 'medium'}>
              <WifiIcon
                sx={{
                  color:
                    connectionStatus === 'connected'
                      ? 'success.main'
                      : connectionStatus === 'connecting'
                        ? 'warning.main'
                        : connectionStatus === 'reconnecting'
                          ? 'warning.main'
                          : 'error.main',
                  opacity: connectionStatus === 'disconnected' ? 0.5 : 1
                }}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('conversations.search.tooltip', 'Search messages')}>
            <IconButton
              size={isMobile ? 'small' : 'medium'}
              onClick={toggleMessageSearchDrawer}
              disabled={!conv}
              aria-label={t('conversations.search.tooltip', 'Search messages')}
            >
              <SearchIcon />
            </IconButton>
          </Tooltip>
          <ColorModeIconDropdown />
          <Tooltip title={t('layout.navbar.language.switch')}>
            <IconButton size={isMobile ? 'small' : 'medium'} onClick={handleLanguageMenuOpen}>
              <LanguageIcon />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={langMenuAnchor}
            open={Boolean(langMenuAnchor)}
            onClose={handleLanguageMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
          >
            <MenuItem onClick={() => handleLanguageChange('vi')} selected={language === 'vi'}>
              {t('layout.navbar.language.vietnamese')}
            </MenuItem>
            <MenuItem onClick={() => handleLanguageChange('en')} selected={language === 'en'}>
              {t('layout.navbar.language.english')}
            </MenuItem>
          </Menu>
          <IconButton size={isMobile ? 'small' : 'medium'} onClick={toggleAside}>
            <MoreVertIcon />
          </IconButton>
        </Stack>
      </Toolbar>
    </AppBar>
  );
};
